import { Injectable } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import {
    DatiqGraphSeriesDataTypes,
    DatiqGraphSeriesType,
    emptyDatiqGraphGridX,
    emptyDatiqGraphGridY,
    IDatiqGraph,
    IDatiqGraphAxis,
    IDatiqGraphSeries,
    IDatiqGraphSeriesData,
} from '../models';
import {
    isDate,
    isEmpty,
    noop
} from 'lodash';
import { DatiqAmcharts5GridService } from './datiq-amcharts-5-grid.service';
import {
    datGrey,
    DatiqFontFamily,
    datiqFontFamilyCaption,
} from '@dat/datiq-components-pkg/utils';
import { DatiqAmcharts5UtilsService } from './datiq-amcharts-5-utils.service';

@Injectable({
    providedIn: 'root',
})
export class DatiqAmcharts5AxisUtilsService {
    constructor(
        private _grid: DatiqAmcharts5GridService,
        private _util: DatiqAmcharts5UtilsService
    ) {}

    setUpYAxisLabel(
        axisY: am5xy.ValueAxis<am5xy.AxisRendererY>,
        axis: IDatiqGraphAxis,
        root: am5.Root
    ): this {
        !!axis?.label?.length ? this.addYAxisLabel(axisY, axis, root) : noop();

        return this;
    }

    setUpXAxisLabel(
        axisX:
            | am5xy.DateAxis<am5xy.AxisRendererX>
            | am5xy.ValueAxis<am5xy.AxisRendererX>,
        axis: IDatiqGraphAxis,
        root: am5.Root
    ): this {
        !!axis?.label?.length ? this.addXAxisLabel(axisX, axis, root) : noop();

        return this;
    }
    addYAxisLabel(
        axisY: am5xy.ValueAxis<am5xy.AxisRendererY>,
        axis: IDatiqGraphAxis,
        root: am5.Root
    ): this {
        const label: am5.Label = am5.Label.new(root, {
            rotation: -90,
            text: axis?.label,
            x: 8,
            y: am5.p50,
            centerX: am5.p50,
            ...this.setUpAxisFont(),
        });

        axisY.children.unshift(label);

        return this;
    }
    addXAxisLabel(
        axisX:
            | am5xy.DateAxis<am5xy.AxisRendererX>
            | am5xy.ValueAxis<am5xy.AxisRendererX>,
        axis: IDatiqGraphAxis,
        root: am5.Root
    ): this {
        const label: am5.Label = am5.Label.new(root, {
            text: axis?.label,
            x: am5.p50,
            centerX: am5.p50,
            ...this.setUpAxisFont(),
        });

        !!axis?.label?.length ? axisX.children.unshift(label) : noop();

        return this;
    }
    setUpAxisFont(): am5.ILabelSettings {
        return {
            fontSize: datiqFontFamilyCaption.fontSize,
            fontWeight: datiqFontFamilyCaption.fontWeight,
            fontFamily: DatiqFontFamily,
            fill: this._util.setColor(datGrey['500']),
        };
    }

    setUpYAxis(axisY: am5xy.ValueAxis<am5xy.AxisRendererY>) {
        const dataItem = axisY.makeDataItem({ value: 0 });
        const range = axisY.createAxisRange(dataItem);

        range.get('grid').setAll({
            stroke: this._util.setColor(datGrey['200']),
            strokeOpacity: 1,
            strokeDasharray: null,
            strokeWidth: 1,
            location: 0.5,
            layer: 10,
        });

        return this;
    }

    setYAxisRenderer(
        root: am5.Root,
        axis: IDatiqGraphAxis
    ): am5xy.AxisRendererY {
        const axisGridConfig = this.getAxisDensity(axis);
        const renderer: am5xy.AxisRendererY = am5xy.AxisRendererY.new(root, {
            ...axisGridConfig,
        });

        this._grid.setGridTemplateY(renderer, { ...emptyDatiqGraphGridY });

        return renderer;
    }

    setXAxisRenderer(
        root: am5.Root,
        axis: IDatiqGraphAxis
    ): am5xy.AxisRendererX {
        const axisGridConfig = this.getAxisDensity(axis);
        const renderer: am5xy.AxisRendererX = am5xy.AxisRendererX.new(root, {
            ...axisGridConfig,
        });

        this._grid.setGridTemplateX(renderer, { ...emptyDatiqGraphGridX });

        return renderer;
    }

    mapDataList(dataList: IDatiqGraphSeriesData[]): unknown[] {
        return dataList?.map((data: IDatiqGraphSeriesData): unknown => {
            return this.mapData(data);
        });
    }

    mergeXAxisData(
        newData: unknown[],
        existingData: am5.ListData<unknown>
    ): unknown[] {
        return [
            ...existingData.values,
            // Return data that have unique keys that don't exist in "existingData"
            ...(newData.reduce((acc: any[], data: any) => {
                if (!existingData.values.some((val: any) => val.x === data.x)) {
                    acc.push(data);
                }
                return acc;
            }, []) as unknown[]),
        ];
    }

    truncateXAxisLabels(axis: am5xy.Axis<any>) {
        axis.get('renderer').labels.template.adapters.add(
            'text',
            (text, target) => {
                if (target.dataItem && target.dataItem.dataContext) {
                    return target.dataItem.dataContext?.x?.split(' ')[0];
                }
                return text;
            }
        );
    }

    mapData(data: IDatiqGraphSeriesData): unknown {
        return !isEmpty(data)
            ? Object.entries(data).reduce(
                  (
                      mappedData: object,
                      [key, value]: [string, DatiqGraphSeriesDataTypes]
                  ) => {
                      return { ...mappedData, [key]: this.mapDataValue(value) };
                  },
                  data
              )
            : data;
    }
    mapDataValue(value: DatiqGraphSeriesDataTypes): any {
        return isDate(value) ? value.getTime() : value;
    }

    setDefaultLabelStyles(renderer: am5xy.AxisRenderer): this {
        renderer.labels.template.set(
            'fontSize',
            datiqFontFamilyCaption.fontSize
        );
        renderer.labels.template.set(
            'fontWeight',
            datiqFontFamilyCaption.fontWeight
        );
        renderer.labels.template.set('fontFamily', DatiqFontFamily);

        renderer.labels.template.set(
            'fill',
            this._util.setColor(datGrey['500'])
        );

        return this;
    }

    setUpXAxis(axisX: am5xy.Axis<am5xy.AxisRendererX>) {
        return this;
    }

    setUpXAxisRenderer(renderer: am5xy.AxisRendererX, settings: IDatiqGraph) {
        renderer.labels.template.set('paddingTop', 8);
        settings?.series
            .filter(
                (serie: IDatiqGraphSeries) =>
                    serie?.type === DatiqGraphSeriesType.Scatter
            )
            .some((scatterSerie: IDatiqGraphSeries) => {
                return scatterSerie.data?.length >= 1;
            })
            ? renderer.labels.template.set('location', 0)
            : noop();
        return this;
    }

    setUpYAxisRenderer(renderer: am5xy.AxisRendererY) {
        renderer.labels.template.set('paddingRight', 8);

        return this;
    }

    getAxisDensity(axis: IDatiqGraphAxis): am5xy.IAxisRendererSettings {
        // return the minimum distance between grid lines in pixels.
        if (!!axis?.axisDensity) {
            return {
                minGridDistance: axis.axisDensity,
            };
        }
        return {};
    }
}
